import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  MenuBook as MenuBookIcon,
  Payments as PaymentsIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import { isAccountingPreviewEnabled, isExpenseCaptureEnabled } from '../../config/featureFlags';
import ExpenseLedger from '../expenses/ExpenseLedger';
import ExpenseReceipts from '../expenses/ExpenseReceipts';
import AccountingPreview from './AccountingPreview';
import {
  consumeLedgerNav,
  FIN_LEDGER_NAV_KEY,
  formatCents,
  getFinancialSummary,
  stashLedgerNav
} from './api';
import TenantLedgerView from './TenantLedgerView';

/**
 * ZRF-FIN Financial Center — dashboard + tenant ledger (flag-gated in App.js).
 */
function FinancialCenter({ initialView = null, quickbooksLink = null, onQuickbooksLinkUsed }) {
  const [view, setView] = useState(initialView || 'home');
  const [ledgerCtx, setLedgerCtx] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFinancialSummary();
      setSummary(data);
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to load summary';
      setError(msg);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const open = (detail) => {
      if (!detail?.tenantId || !detail?.propertyId) return;
      setLedgerCtx(detail);
      setView('ledger');
    };
    const fromStorage = consumeLedgerNav();
    if (fromStorage) open(fromStorage);

    const onEvent = (e) => open(e.detail);
    window.addEventListener('zrf-fin-open-ledger', onEvent);
    return () => window.removeEventListener('zrf-fin-open-ledger', onEvent);
  }, []);

  useEffect(() => {
    if (view === 'home') loadSummary();
  }, [view, loadSummary]);

  if (view === 'receipts') {
    return <ExpenseReceipts onBack={() => setView('home')} />;
  }

  if (view === 'expenses') {
    return <ExpenseLedger onBack={() => setView('home')} onOpenReceipts={() => setView('receipts')} />;
  }

  if (view === 'quickbooks' && isAccountingPreviewEnabled()) {
    return (
      <AccountingPreview
        onBack={() => setView('home')}
        quickbooksLink={quickbooksLink}
        onQuickbooksLinkUsed={onQuickbooksLinkUsed}
      />
    );
  }

  if (view === 'ledger' && ledgerCtx) {
    return (
      <TenantLedgerView
        tenantId={ledgerCtx.tenantId}
        propertyId={ledgerCtx.propertyId}
        unitId={ledgerCtx.unitId}
        leaseId={ledgerCtx.leaseId}
        tenantLabel={ledgerCtx.tenantLabel}
        onBack={() => {
          setView('home');
          setLedgerCtx(null);
          try {
            sessionStorage.removeItem(FIN_LEDGER_NAV_KEY);
          } catch (_) {
            /* ignore */
          }
        }}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <AccountBalanceIcon color="primary" fontSize="large" />
        <Typography variant="h5" component="h1">
          Financial Center
        </Typography>
        {(isExpenseCaptureEnabled() || isAccountingPreviewEnabled()) && (
          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }} useFlexGap flexWrap="wrap">
            {isExpenseCaptureEnabled() && (
              <>
                <Button variant="outlined" startIcon={<PaymentsIcon />} onClick={() => setView('expenses')}>
                  Expenses
                </Button>
                <Button variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => setView('receipts')}>
                  Receipts & bills
                </Button>
              </>
            )}
            {isAccountingPreviewEnabled() && (
              <Button variant="outlined" startIcon={<MenuBookIcon />} onClick={() => setView('quickbooks')}>
                QuickBooks
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Create a rent ledger from an active lease on <strong>Leases</strong>, or
        open a tenant from <strong>Tenants</strong>. Payments allocate to oldest
        outstanding schedule lines and generate a receipt number.
      </Alert>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Ensure API FINANCIALS_ENABLED=true matches this CRA flag.
          </Typography>
        </Alert>
      )}

      {summary && !loading && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3, flexWrap: 'wrap' }}
          >
            {[
              {
                label: 'Rent due this month',
                value: summary.rentDueThisMonthCents
              },
              {
                label: 'Collected this month',
                value: summary.rentCollectedThisMonthCents
              },
              {
                label: 'Outstanding',
                value: summary.outstandingBalanceCents
              },
              {
                label: 'Deposits held',
                value: summary.depositsHeldCents
              }
            ].map((card) => (
              <Box
                key={card.label}
                sx={{
                  flex: '1 1 160px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h6">
                  {formatCents(card.value, summary.currency)}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Balances due
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Property</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.overdueTenants || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No outstanding tenant balances.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {(summary.overdueTenants || []).map((row) => (
                <TableRow key={`${row.tenantId}-${row.propertyId}`}>
                  <TableCell>{row.tenantName || row.tenantId}</TableCell>
                  <TableCell>{row.propertyName || row.propertyId}</TableCell>
                  <TableCell align="right">
                    {formatCents(row.balanceDueCents, row.currency)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => {
                        const ctx = {
                          tenantId: row.tenantId,
                          propertyId: row.propertyId,
                          tenantLabel: row.tenantName || undefined
                        };
                        stashLedgerNav(ctx);
                        setLedgerCtx(ctx);
                        setView('ledger');
                      }}
                    >
                      View ledger
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Recent payments
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.recentPayments || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No payments yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {(summary.recentPayments || []).map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.paymentDate}</TableCell>
                  <TableCell>{p.tenantName || p.tenantId}</TableCell>
                  <TableCell>{p.methodType}</TableCell>
                  <TableCell align="right">
                    {formatCents(p.amountCents, p.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}

export { CreateRentLedgerDialog } from './CreateRentLedgerDialog';
export default FinancialCenter;
